import {
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  makeStyles,
  TextField,
  withStyles
} from "@material-ui/core";
import Checkbox, { CheckboxProps } from "@material-ui/core/Checkbox";
import { grey, yellow } from "@material-ui/core/colors";
import StarIcon from "@material-ui/icons/Star";
import CardTitle from "@saleor/components/CardTitle";
import MultiAutocompleteChipsSelectField from "@saleor/components/MultiAutocompleteChipField/MultiAutocompleteChipField";
import RichTextEditor from "@saleor/components/RichTextEditor";
import { parseRawDraftContentState } from "@saleor/components/RichTextEditor/rawDraftContentState";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { COUPON_COLLECTION_TYPE_CHOICES } from "@saleor/coupons/constants";
import { CouponCollectionDetailQuery_couponCollection } from "@saleor/coupons/types/CouponCollectionDetailQuery";
import { FormChange } from "@saleor/hooks/useForm";
import useNotifier from "@saleor/hooks/useNotifier";
import { CouponCollectionCategoryEnum } from "@saleor/types/globalTypes";
import { CouponCollectionFormData } from "@saleor/utils/validators/couponCollection";
import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    cardTitle: {
      "& span": {
        fontWeight: "bolder"
      }
    },
    helperText: {
      "& span": {
        color: grey[800]
      },
      color: "#EC5D77",
      margin: "2px 0px"
    },
    mainCard: {
      marginBottom: "20px"
    },
    requiredSymbol: {
      color: "red"
    },
    subtitle: {
      fontSize: "1rem"
    },
    textField: {
      marginTop: "20px"
    },
    textFieldGrid: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridTemplateColumns: "1fr 1fr"
    },
    textFieldPadding: {
      marginBottom: "20px"
    }
  }),
  { name: "CouponCollectionDetails" }
);

export interface CouponCollectionDetailsProps {
  data: CouponCollectionFormData;
  collection?: CouponCollectionDetailQuery_couponCollection;
  disabled: boolean;
  onChange: FormChange;
  error?: {
    title: string;
    categories?: string;
    classification?: string;
    writeup?: string;
  };
  mode?: "create" | "edit";
}

const handleCouponCategories = (categories, onChange, data, collection) => {
  const notify = useNotifier();
  const allCategory = [
    {
      label: "All Categories",
      value: "ALL"
    }
  ];

  const handleCategoryChange = event => {
    const { name, value } = event.target;
    if (data.categories.includes(value)) {
      handleCategoryRemove(event);
    } else {
      if (data.status === "INACTIVE") {
        const currentSelected = [
          ...new Set([...collection, ...data.categories, value])
        ];
        onChange({
          target: {
            name,
            value: currentSelected
          }
        });
      } else {
        onChange({
          target: {
            name,
            value: [...data.categories, value]
          }
        });
      }
    }
    if (value === "ALL") {
      onChange({
        target: {
          name,
          value: []
        }
      });
    }
  };

  const handleCategoryRemove = event => {
    const { name, value } = event.target;
    if (collection.includes(value) && data.status === "INACTIVE") {
      return;
    }
    if (value === "ALL") {
      notify({
        status: "error",
        text: "Categories cannot be empty",
        title: "Collection Form Error"
      });
    } else {
      onChange({
        target: {
          name,
          value: data.categories.filter(
            category => category.toLocaleUpperCase() !== value
          )
        }
      });
    }
  };

  const currentCategories = (data, choices) =>
    data?.categories
      ?.map(selectedCat => {
        const selectCat = selectedCat.toLocaleUpperCase();
        const foundCat = choices.find(cat => cat.value === selectCat);
        return foundCat
          ? { label: foundCat.label, value: foundCat.value }
          : null;
      })
      .filter(cat => cat !== null);

  const currentCats =
    data?.categories?.length === categories?.length
      ? allCategory
      : currentCategories(data, categories);

  return [currentCats, handleCategoryChange, handleCategoryRemove];
};

const FeatureCheckbox = withStyles(
  {
    checked: {},
    root: {
      "&$checked": {
        color: yellow[600]
      },
      color: grey[400]
    }
  },
  { name: "FeatureCheckbox" }
)((props: CheckboxProps) => <Checkbox color="default" {...props} />);

export const CouponCollectionDetails: React.FC<CouponCollectionDetailsProps> = props => {
  const {
    data,
    onChange,
    error,
    disabled,
    collection,
    mode = "create"
  } = props;
  const intl = useIntl();
  const classes = useStyles({});
  const titleField = useRef(null);

  const couponCategoryChoices = [
    { label: "Dining", value: CouponCollectionCategoryEnum.DINING },
    { label: "Cyberzone", value: CouponCollectionCategoryEnum.CYBERZONE },
    { label: "Wellness", value: CouponCollectionCategoryEnum.WELLNESS },
    {
      label: "Entertainment",
      value: CouponCollectionCategoryEnum.ENTERTAINMENT
    },
    { label: "Shopping", value: CouponCollectionCategoryEnum.SHOPPING },
    { label: "Groceries", value: CouponCollectionCategoryEnum.GROCERIES },
    { label: "Events", value: CouponCollectionCategoryEnum.EVENTS }
  ];

  const handleKeyPress = e => {
    if (/[`[/!@#$%^&*()_+\=\]{};:"\\|,.<>?~]/.test(e.key)) {
      e.preventDefault();
    }
  };
  const [, setTagCategoryText] = useState("");

  const savedCategories = collection?.categories ?? [];

  const [
    currentCats,
    handleCategoryChange,
    handleCategoryRemove
  ] = handleCouponCategories(
    couponCategoryChoices,
    onChange,
    data,
    savedCategories
  );

  const allCategory = {
    label: "All Categories",
    value: "ALL"
  };

  const initialWriteup = React.useMemo(
    () =>
      collection?.writeup
        ? parseRawDraftContentState(collection.writeup)
        : undefined,
    [collection?.writeup]
  );

  useEffect(() => {
    if (
      (data.status === "DRAFT" && data.categories !== undefined) ||
      (data.status === "" && data.categories !== undefined) ||
      (data.status === "INACTIVE" && data.categories !== undefined)
    ) {
      if (data?.categories?.length === couponCategoryChoices.length) {
        onChange({
          target: {
            name: "categories",
            value: []
          }
        });
      }
    }
  }, [data.categories]);

  const handleChangeCollectionType = event => {
    onChange(event);
    onChange({ target: { name: "categories", value: [] } });
  };

  return (
    <Card className={classes.mainCard}>
      <CardTitle
        className={classes.cardTitle}
        title={intl.formatMessage({
          defaultMessage: "Coupon Collection Details",
          description: "section header"
        })}
        toolbar={
          <FormControlLabel
            control={
              <FeatureCheckbox
                id="isFeaturedCheckbox"
                icon={<StarIcon />}
                checkedIcon={<StarIcon />}
                name="isFeatured"
                color="primary"
                checked={data.isFeatured}
                onChange={e => {
                  onChange({
                    target: {
                      name: e.target.name,
                      value: e.target.checked
                    }
                  });
                }}
                disabled={disabled}
              />
            }
            label="Set as Featured"
            labelPlacement="end"
          />
        }
      />
      <CardContent>
        <TextField
          ref={titleField}
          label={
            <>
              <span>Coupon Collection Title </span>
              <span className={classes.requiredSymbol}>*</span>
            </>
          }
          value={data.title}
          onChange={onChange}
          name="title"
          fullWidth
          error={!!error.title}
          helperText={error.title ? "This field is required." : ""}
          inputProps={{
            maxLength: 50
          }}
          FormHelperTextProps={{
            className: classes.helperText
          }}
          type="text"
          className={classes.textFieldPadding}
          disabled={disabled}
        />
        <RichTextEditor
          disabled={disabled}
          error={!!error.writeup}
          initial={initialWriteup}
          label={
            <>
              <span>Short Description </span>
              <span className={classes.requiredSymbol}>*</span>
            </>
          }
          name="writeup"
          onChange={onChange}
          helperText={error.writeup ? "This field is required." : ""}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <SingleSelectField
              className={classes.textField}
              name="collectionType"
              disabled={disabled || mode === "edit"}
              choices={COUPON_COLLECTION_TYPE_CHOICES}
              onChange={handleChangeCollectionType}
              label={
                <>
                  <span>Coupon Collection Type </span>
                  <span className={classes.requiredSymbol}>*</span>
                </>
              }
              value={data.collectionType}
            />
          </Grid>
          <Grid item xs={12} sm={6} className={classes.textField}>
            <MultiAutocompleteChipsSelectField
              limit={
                data.status === "INACTIVE" && savedCategories.length === 0
                  ? 0
                  : undefined
              }
              name="categories"
              label={
                <>
                  <span>Coupon Collection Category </span>
                  <span className={classes.requiredSymbol}>*</span>
                </>
              }
              disabled={
                (disabled && data.status !== "INACTIVE") ||
                data.collectionType === COUPON_COLLECTION_TYPE_CHOICES[0].value
              }
              displayValues={currentCats}
              choices={couponCategoryChoices}
              value={currentCats}
              onChange={handleCategoryChange}
              onKeyPress={handleKeyPress}
              onRemove={handleCategoryRemove}
              onChangeText={setTagCategoryText}
              hasSelectAll={true}
              selectAllProps={allCategory}
            />
          </Grid>
        </Grid>
        <TextField
          label="Slug"
          value={data.slug || ""}
          onChange={onChange}
          name="slug"
          fullWidth
          inputProps={{
            maxLength: 100
          }}
          type="text"
          className={classes.textField}
          disabled={disabled}
        />
      </CardContent>
    </Card>
  );
};

export default CouponCollectionDetails;
